import { Link } from 'react-router-dom';
import { useAppData } from '../store';
import { bucketObligations } from '../lib/status';
import ObligationCard from './ObligationCard';
import ProjectCard from './ProjectCard';
import NothingFallsThroughSummary from './NothingFallsThroughSummary';

function Section({
  title,
  emoji,
  obligations,
  remindersByObligation,
  emptyText,
}: {
  title: string;
  emoji: string;
  obligations: ReturnType<typeof bucketObligations>['overdue'];
  remindersByObligation: ReturnType<typeof useAppData>['remindersByObligation'];
  emptyText?: string;
}) {
  if (!obligations.length && !emptyText) return null;
  return (
    <section className="dashboard-section">
      <h2>
        {emoji} {title}
      </h2>
      {obligations.length === 0 ? (
        <p className="muted empty">{emptyText}</p>
      ) : (
        <div className="card-list">
          {obligations.map((o) => (
            <ObligationCard key={o.id} obligation={o} reminders={remindersByObligation[o.id]} />
          ))}
        </div>
      )}
    </section>
  );
}

export default function Dashboard() {
  const { obligations, remindersByObligation, projects, loading, error } = useAppData();

  if (loading) return <div className="loading-screen">Loading…</div>;
  if (error) return <div className="error-screen">Couldn't load your data: {error}</div>;

  const buckets = bucketObligations(obligations);
  const isEmpty = obligations.length === 0;
  const activeProjects = projects.filter((p) => p.status === 'planned' || p.status === 'in_progress');

  return (
    <div className="dashboard">
      <NothingFallsThroughSummary />

      {activeProjects.length > 0 && (
        <section className="dashboard-section">
          <div className="section-header">
            <h2>🛠️ Active Projects</h2>
            <Link to="/projects" className="muted">
              See all
            </Link>
          </div>
          <div className="card-list">
            {activeProjects.map((p) => (
              <ProjectCard key={p.id} project={p} />
            ))}
          </div>
        </section>
      )}

      {isEmpty && (
        <div className="empty-state card">
          <h2>Nothing tracked yet</h2>
          <p className="muted">
            Add your first asset, then add what needs to be remembered for it.
          </p>
          <Link to="/assets" className="btn-primary">
            Get started
          </Link>
        </div>
      )}

      <Section
        title="Overdue"
        emoji="🔴"
        obligations={buckets.overdue}
        remindersByObligation={remindersByObligation}
      />
      <Section
        title="Today"
        emoji="📍"
        obligations={buckets.today}
        remindersByObligation={remindersByObligation}
      />
      <Section
        title="Coming Up"
        emoji="🟠"
        obligations={buckets.comingUp}
        remindersByObligation={remindersByObligation}
      />
      <Section
        title="Later"
        emoji="🟢"
        obligations={buckets.later}
        remindersByObligation={remindersByObligation}
      />
    </div>
  );
}
